import { supabase } from '@/integrations/supabase/client';
import type { BillingInterval, BillingState, BillingPortalSession, CheckoutSession } from '@/types/payment';

async function invoke<T>(functionName: string, body: Record<string, unknown> = {}, authenticated = true): Promise<T> {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (authenticated && (sessionError || !session)) throw new Error('Please sign in to manage billing.');
  const { data, error } = await supabase.functions.invoke(functionName, {
    body, ...(authenticated ? { headers: { Authorization: 'Bearer ' + session!.access_token } } : {}),
  });
  if (error) {
    let message = 'Billing could not be updated. Please retry shortly.';
    if (error.context instanceof Response) {
      try { message = (await error.context.json()).error || message; } catch { /* Keep fallback for non-JSON errors. */ }
    }
    throw new Error(message);
  }
  if (!data || data.error) throw new Error(data?.error || 'Billing returned an empty response.');
  return data as T;
}

export const paymentAPI = {
  getPrices: (): Promise<{ prices: { planId: string; billingInterval: BillingInterval; currency: string; amount: number }[] }> =>
    invoke('stripe-pricing', {}, false),
  createCheckoutSession: (request: { planId: string; billingInterval: BillingInterval }): Promise<CheckoutSession> =>
    invoke('stripe-create-checkout', { planId: request.planId, billingInterval: request.billingInterval }),
  createPortalSession: (): Promise<BillingPortalSession> => invoke('stripe-create-portal'),
  getBillingState: (checkoutSessionId?: string): Promise<BillingState> =>
    invoke('stripe-billing-state', checkoutSessionId ? { checkoutSessionId } : {}),
};
